import { getCurrentToken } from './index';

const DEFAULT_MAX_ATTEMPTS = 3;
const DEFAULT_RETRY_DELAY_MS = 500;
const KALDI_STT_URL = 'https://kaldi-stt-api.cloubot.com/process';

const wait = (delayMs) => new Promise((resolve) => setTimeout(resolve, delayMs));

const getSafeErrorMessage = (error) => {
  try {
    if (typeof error === 'string') return error;
    if (typeof error?.message === 'string') return error.message;
  } catch (_error) {
    // Some browser-native errors throw while their properties are inspected.
  }

  return 'Unknown browser error';
};

export const createRecordingObjectUrl = (blob) => {
  if (!blob || typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return null;

  try {
    return URL.createObjectURL(blob);
  } catch (error) {
    console.warn(`Unable to create a recording preview URL: ${getSafeErrorMessage(error)}`);
    return null;
  }
};

export const getWavDurationMs = async (blob) => {
  if (!blob?.slice || !blob.size) return null;

  try {
    const headerBlob = blob.slice(0, Math.min(blob.size, 65536));
    const buffer =
      typeof headerBlob.arrayBuffer === 'function'
        ? await headerBlob.arrayBuffer()
        : await new Response(headerBlob).arrayBuffer();
    const view = new DataView(buffer);
    const readChunkName = (offset) =>
      String.fromCharCode(view.getUint8(offset), view.getUint8(offset + 1), view.getUint8(offset + 2), view.getUint8(offset + 3));

    if (view.byteLength < 12 || readChunkName(0) !== 'RIFF' || readChunkName(8) !== 'WAVE') return null;

    let byteRate = 0;
    let dataSize = 0;
    let offset = 12;
    while (offset + 8 <= view.byteLength) {
      const chunkName = readChunkName(offset);
      const chunkSize = view.getUint32(offset + 4, true);
      const dataOffset = offset + 8;

      if (chunkName === 'fmt ' && chunkSize >= 12 && dataOffset + 12 <= view.byteLength) {
        byteRate = view.getUint32(dataOffset + 8, true);
      } else if (chunkName === 'data') {
        dataSize = Math.min(chunkSize, Math.max(blob.size - dataOffset, 0));
        break;
      }

      offset = dataOffset + chunkSize + (chunkSize % 2);
    }

    return byteRate > 0 && dataSize > 0 ? (dataSize / byteRate) * 1000 : null;
  } catch (_error) {
    return null;
  }
};

const getErrorMessage = (responseData, fallback) => {
  if (typeof responseData?.message === 'string' && responseData.message) {
    return responseData.message;
  }
  return fallback;
};

export const processKaldiSttWithRetry = async (
  formData,
  { maxAttempts = DEFAULT_MAX_ATTEMPTS, retryDelayMs = DEFAULT_RETRY_DELAY_MS } = {},
) => {
  let lastError;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      const token = getCurrentToken();
      const response = await fetch(KALDI_STT_URL, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: formData,
      });
      const responseData = await response.json().catch(() => null);

      if (response.ok && typeof responseData?.text === 'string') {
        return responseData;
      }

      lastError = new Error(
        getErrorMessage(responseData, `Speech recognition request failed with status ${response.status}`),
      );
    } catch (error) {
      lastError = error;
    }

    if (attempt < maxAttempts) {
      await wait(retryDelayMs * attempt);
    }
  }

  throw lastError || new Error('Speech recognition request failed');
};

export const uploadRecordingWithRetry = async (
  createSignedUpload,
  { name, blob, contentType = 'audio/wav', maxAttempts = DEFAULT_MAX_ATTEMPTS, retryDelayMs = DEFAULT_RETRY_DELAY_MS },
) => {
  let lastError;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      const signedUploadUrl = await createSignedUpload({ name, contentType, replace: true });

      if (!signedUploadUrl) {
        throw new Error('Signed S3 upload URL was not returned');
      }

      const uploadResponse = await fetch(signedUploadUrl, {
        method: 'PUT',
        body: blob,
        headers: {
          'Content-Type': contentType,
          'x-amz-acl': 'public-read',
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Recording upload failed with status ${uploadResponse.status}`);
      }

      return signedUploadUrl.split('?')[0];
    } catch (error) {
      lastError = error;
    }

    if (attempt < maxAttempts) {
      await wait(retryDelayMs * attempt);
    }
  }

  throw lastError || new Error('Recording upload failed');
};
